import { isChannelMuted } from '@common/helpers/channelNotifications'
import { IRecentConversationItem } from '@common/interfaces/channels'
import { Middleware } from '@reduxjs/toolkit'
import { Action } from 'redux'
import { listRecentConversations } from './channels/actions'
import { IRootState } from './reducer'

/** setTimeout overflows past this (about 24.8 days) and fires immediately. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/** The soonest still-open mute window across the rows, their own or their group's. */
export const nextMuteExpiry = (items: readonly IRecentConversationItem[]): number | null => {
  let next: number | null = null
  for (const item of items) {
    for (const until of [item.mutedUntil, item.groupSettings?.mutedUntil]) {
      if (!until || !isChannelMuted(until)) continue
      const at = new Date(until).getTime()
      if (next === null || at < next) next = at
    }
  }
  return next
}

/**
 * Retry gaps when the server still reports a mute the client clock calls over: the
 * client is ahead. Bounded, so a badly skewed clock can't poll the list for minutes;
 * past the last one, ordinary refetches (socket frames, focus) pick the change up.
 */
export const SERVER_EXPIRY_RETRY_MS = [2000, 10_000, 30_000]

const soonestOf = (a: number | null, b: number | null): number | null => {
  if (a === null) return b
  if (b === null) return a
  return Math.min(a, b)
}

/**
 * Muted rows drop their badge and their unread from every total, and whether a timed
 * mute is still open depends on the clock. Nothing in the store changes when one runs
 * out, so this refetches the list at that moment: the server's total also covers rows
 * past the loaded window, which no local re-sum can.
 */
const muteExpiryMiddleware = (): Middleware<object, IRootState> => (store) => {
  let lastItems: readonly IRecentConversationItem[] | null = null
  let scheduledAt: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const schedule = (at: number | null) => {
    if (at === scheduledAt) return
    if (timer) clearTimeout(timer)
    timer = null
    scheduledAt = at
    if (at === null) return
    const delay = Math.min(Math.max(at - Date.now(), 0) + 50, MAX_TIMEOUT_MS)
    timer = setTimeout(() => {
      // A skew retry spends its attempt only now, when it actually refetches.
      if (at === skewRetry.retryAt) {
        skewRetry = { ...skewRetry, count: skewRetry.count + 1, retryAt: null }
      }
      timer = null
      scheduledAt = null
      store.dispatch(listRecentConversations() as unknown as Action)
    }, delay)
  }

  let lastServerExpiry: string | null = null
  let skewRetry: { expiry: string; count: number; retryAt: number | null } = {
    expiry: '',
    count: 0,
    retryAt: null,
  }

  /**
   * Read against the server's clock, not ours: a client running ahead would refetch
   * before the server's mute ended, get the same expiry back, call it past and stop.
   */
  const serverExpiryAt = (iso: string | null): number | null => {
    if (!iso) return null
    const at = new Date(iso).getTime()
    if (at > Date.now()) return at
    if (skewRetry.expiry !== iso) skewRetry = { expiry: iso, count: 0, retryAt: null }
    // Kept across re-evaluations, so unrelated list updates can't push it back or spend it.
    if (skewRetry.retryAt !== null) return skewRetry.retryAt
    const gap = SERVER_EXPIRY_RETRY_MS[skewRetry.count]
    if (gap === undefined) return null
    skewRetry = { ...skewRetry, retryAt: Date.now() + gap }
    return skewRetry.retryAt
  }

  return (next) => (action) => {
    const result = next(action)
    const { items, nextMuteExpiry: serverExpiry } = store.getState().channels.recentConversations
    if (items !== lastItems || serverExpiry !== lastServerExpiry) {
      lastItems = items
      lastServerExpiry = serverExpiry
      schedule(soonestOf(nextMuteExpiry(items), serverExpiryAt(serverExpiry)))
    }
    return result
  }
}

export default muteExpiryMiddleware
